import { isValidElement, useEffect, useRef } from "react";
import { useCartState } from "@made-people/grease-cart";
import { useSyncEmarsysContactCart } from "./emarsys-contact-cart-sync";
import { useSetVisitorEmail } from "./mm-visitor-email-provider";
import type { ReactNode } from "react";

type KustomCheckoutApi = {
  on: (listeners: { change: (data: { email?: string }) => void }) => void;
};

type KustomCheckout = (callback: (api: KustomCheckoutApi) => void) => void;

declare global {
  interface Window {
    _klarnaCheckout?: KustomCheckout;
  }
}

const registeredCheckouts = new WeakSet<KustomCheckout>();

const MmKustomVisitorEmailRegistration = () => {
  const { cart } = useCartState();
  const setVisitorEmail = useSetVisitorEmail();
  const syncCart = useSyncEmarsysContactCart();
  const cartRef = useRef(cart);
  const lastEmailRef = useRef<string>();
  cartRef.current = cart;

  useEffect(() => {
    const checkout = window._klarnaCheckout;
    if (typeof checkout !== "function" || registeredCheckouts.has(checkout)) {
      return;
    }

    registeredCheckouts.add(checkout);
    checkout((api) => {
      api.on({
        change: (data) => {
          const email = setVisitorEmail(data.email);
          const currentCart = cartRef.current;
          if (!email || !currentCart || lastEmailRef.current === email) {
            return;
          }

          lastEmailRef.current = email;
          syncCart(email, currentCart).catch((error: unknown) => {
            if (lastEmailRef.current === email) {
              lastEmailRef.current = undefined;
            }
            console.error("Emarsys contact cart sync failed:", error);
          });
        },
      });
    });
  }, [setVisitorEmail, syncCart]);

  return null;
};

/**
 * Ties the visitor e-mail listener to the rendered Kustom checkout instance.
 * Centra changes the checkout element key when the iframe is reinitialized,
 * which remounts the registration and binds it to the new Kustom API instance.
 */
export const MmKustomVisitorEmailListener = (props: {
  checkoutElement: ReactNode;
}) => {
  const checkoutKey =
    isValidElement(props.checkoutElement) && props.checkoutElement.key !== null
      ? props.checkoutElement.key
      : "kustom-checkout";

  return <MmKustomVisitorEmailRegistration key={checkoutKey} />;
};
